import { SyntheticEvent, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import {
    IUpdatePassword,
    UpdatePasswordFormStatus,
    UpdatePasswordPayload,
} from '@interfaces/myProfileInterfaces';

import { updateSettingsPassword } from '@services/myProfileServices';

import { SubmitHandler, useForm } from 'react-hook-form';

import PasswordCheckList from '@components/styled-UI/password-checklist/PasswordCheckList';

import { TextField, FormHelperText, InputAdornment } from '@mui/material';

import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import VisibilityOff from '@images/svg-icons/visibilityOff.svg';
import VisibilityOn from '@images/svg-icons/visibilityOn.svg';
import DefaultIcon from '@images/svg-icons/defaultIcon.svg';
import SuccessIconNoBackground from '@images/svg-icons/successIconNoBackground.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import * as actions from '@redux-actions/';

import { getLabelFallback } from '@utils/utils';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

interface ShowPasswords {
    showCurrentPassword: boolean;
    showCurrentPasswordConfirmed: boolean;
}

const UpdatePassword = (): JSX.Element => {
    const schema: yup.SchemaOf<IUpdatePassword> = yup.object().shape({
        currentPassword: yup
            .string()
            .required(
                getLabelFallback('SETTINGS_UPDATE_PASSWORD_CURRENTPASSWORD_EMPTY_FIELD_MESSAGE')
            ),
        newPassword: yup
            .string()
            .required(getLabelFallback('SETTINGS_UPDATE_PASSWORD_NEWPASSWORD_EMPTY_FIELD_MESSAGE'))
            .test(
                'invalid',
                getLabelFallback('SETTINGS_UPDATE_PASSWORD_MINIMUM_REQUIREMENTS_ERROR_MESSAGE'),
                value => (value && !isValid ? false : true)
            ),
        newPasswordConfirmed: yup
            .string()
            .required(
                getLabelFallback(
                    'SETTINGS_UPDATE_PASSWORD_NEWPASSWORDCONFIRMED_EMPTY_FIELD_MESSAGE'
                )
            )
            .test(
                'invalid',
                getLabelFallback('SETTINGS_UPDATE_PASSWORD_INVALID_ERROR_MESSAGE'),
                (value, context) =>
                    value && context.parent.newPassword && value != context.parent.newPassword
                        ? false
                        : true
            ),
    });
    const [showPassword, setShowPassword] = useState<ShowPasswords>({
        showCurrentPassword: false,
        showCurrentPasswordConfirmed: false,
    });

    const [isValid, setIsValid] = useState<boolean>(false);

    const config = useSelector((state: RootState) => state.common.config);
    const updateSettingsPasswordRequestStatus = useSelector(
        (state: RootState) => state.myProfile.updateSettingsPasswordRequestStatus
    );

    const errorMessage = useSelector((state: RootState) => state.myProfile.errorMessage);

    const dispatch = useDispatch();

    const {
        register,
        watch,
        handleSubmit,
        formState: { errors, isSubmitting },
        setValue,
        setError,
        clearErrors,
    } = useForm<IUpdatePassword>({
        resolver: yupResolver(schema),
    });
    const watchNewPassword = watch('newPassword');
    const watchNewPasswordConfirmed = watch('newPasswordConfirmed');

    const initialFormStatus: UpdatePasswordFormStatus<Partial<IUpdatePassword>> = {
        currentPassword: {
            success: false,
            focus: false,
        },
        newPassword: {
            success: false,
            focus: false,
        },
        newPasswordConfirmed: {
            success: false,
            focus: false,
        },
    };

    const [formFieldStatus, setFormFieldStatus] =
        useState<UpdatePasswordFormStatus<Partial<IUpdatePassword>>>(initialFormStatus);

    useEffect(() => {
        errorMessage &&
            (setError('currentPassword', {
                type: 'pattern',
                message: getLabelFallback(errorMessage),
            }),
            setFormFieldStatus(initialFormStatus),
            setShowPassword({ showCurrentPassword: false, showCurrentPasswordConfirmed: false }),
            setValue('currentPassword', ''),
            setValue('newPassword', ''),
            setValue('newPasswordConfirmed', ''));
    }, [errorMessage]);

    useEffect(() => {
        return () => {
            dispatch(actions.updateSettingsPasswordCustomError(null));
        };
    }, []);

    /** The function below handles the onBlur, onChange and onFocus events for the form validation */

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        !isSubmitting && clearErrors(event.name as keyof IUpdatePassword);
        if (event.name === 'newPassword' && e.type === 'blur') {
            !isValid &&
                setError('newPassword', {
                    type: 'pattern',
                    message: getLabelFallback(
                        'SETTINGS_UPDATE_PASSWORD_MINIMUM_REQUIREMENTS_ERROR_MESSAGE'
                    ),
                });
            (watchNewPasswordConfirmed || watchNewPassword) &&
                watchNewPasswordConfirmed === watchNewPassword &&
                clearErrors('newPasswordConfirmed');
        }
        if (
            (isSubmitting || e.type === 'blur') &&
            watchNewPasswordConfirmed &&
            watchNewPasswordConfirmed !== watchNewPassword
        ) {
            setError('newPasswordConfirmed', {
                type: 'pattern',
                message: getLabelFallback('SETTINGS_UPDATE_PASSWORD_INVALID_ERROR_MESSAGE'),
            });
        }
        event.value
            ? (setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: {
                      success: e.type === 'blur' ? true : e.type === 'change' ? false : false,
                      focus:
                          e.type === 'blur'
                              ? false
                              : e.type === 'change'
                              ? true
                              : e.type === 'focus' && !errors[event.name]
                              ? true
                              : false,
                  },
              }),
              setValue(event.name as keyof IUpdatePassword, event.value))
            : (setValue(event.name as keyof IUpdatePassword, ''),
              setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: { success: false, focus: false },
              }),
              e.type === 'blur' &&
                  setError(event.name as keyof IUpdatePassword, {
                      type: 'required',
                      message: getLabelFallback(
                          `SETTINGS_UPDATE_PASSWORD_${event.name.toUpperCase()}_EMPTY_FIELD_MESSAGE`
                      ),
                  }));
    };

    const onSubmit: SubmitHandler<IUpdatePassword> = (data: IUpdatePassword) => {
        if (isValid) {
            clearErrors('newPassword');
            const dataToSend: UpdatePasswordPayload = {
                isMockRequest: config.mockRequest,
                storeId: config?.storeId,
                catalogId: config?.catalogId,
                langId: config?.langId,
                URL: 'ContactInformation',
                errorViewName: 'ResetPasswordRegisterErrorView',
                formName: 'PasswordUpdateSubmitForm',
                isResetPasswordRequest: true,
                challengeQuestion: '-',
                challengeAnswer: '-',
                logonPasswordOld: data.currentPassword,
                logonPassword: data.newPassword,
                logonPasswordVerify: data.newPasswordConfirmed,
                logonId: `${config?.storeId}|${config?.userEmail}`,
            };
            dispatch(updateSettingsPassword(dataToSend));
        } else {
            setError('newPassword', {
                type: 'custom',
                message: getLabelFallback('SETTINGS_UPDATE_PASSWORD_INVALID_PASSWORD'),
            });
        }
    };
    return (
        <div role="region" aria-label="Update password form" className="update-password">
            <div className="content-container update-password-form">
                <span className="secondary-title mb-24">
                    {getLabelFallback('SETTINGS_UPDATE_PASSWORD')}
                </span>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-group mb-24">
                        <TextField
                            {...register('currentPassword')}
                            required
                            className={`w-100 ${
                                errors?.currentPassword?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.currentPassword.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="currentPassword"
                            variant="outlined"
                            type="password"
                            autoComplete="on"
                            label={getLabelFallback(
                                'SETTINGS_UPDATE_PASSWORD_CURRENT_PASSWORD_FIELD_LABEL'
                            )}
                            helperText={errors?.currentPassword?.message}
                            InputProps={{
                                endAdornment: formFieldStatus.currentPassword.focus ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('currentPassword', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.currentPassword?.message ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formFieldStatus.currentPassword.success ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </div>
                    <div className="form-group new-password mb-16">
                        <TextField
                            {...register('newPassword')}
                            required
                            className={`w-100 ${
                                errors?.newPassword?.message
                                    ? 'form-field-error'
                                    : isValid && formFieldStatus.newPassword.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="newPassword"
                            variant="outlined"
                            type={showPassword.showCurrentPassword ? 'text' : 'password'}
                            autoComplete="on"
                            label={getLabelFallback('SETTINGS_UPDATE_PASSWORD_NEW_PASSWORD')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        className={`${
                                            errors?.newPassword?.message ||
                                            isValid ||
                                            formFieldStatus.newPassword.focus
                                                ? 'large'
                                                : ''
                                        }`}
                                        position="end"
                                    >
                                        {errors?.newPassword?.message ? (
                                            <ErrorIcon />
                                        ) : isValid && formFieldStatus.newPassword.success ? (
                                            <SuccessIcon />
                                        ) : formFieldStatus.newPassword.focus ? (
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('newPassword', '')}
                                            />
                                        ) : null}
                                        <div
                                            className="input-field-icon"
                                            onClick={() =>
                                                setShowPassword({
                                                    ...showPassword,
                                                    showCurrentPassword:
                                                        !showPassword.showCurrentPassword,
                                                })
                                            }
                                        >
                                            {showPassword.showCurrentPassword ? (
                                                <VisibilityOn />
                                            ) : (
                                                <VisibilityOff />
                                            )}
                                        </div>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormHelperText className="ml-14">
                            {getLabelFallback(
                                'SETTINGS_UPDATE_PASSWORD_CURRENT_PASSWORD_INFORMATION_MESSAGE'
                            )}
                        </FormHelperText>
                        <PasswordCheckList
                            rules={['minLength', 'capital', 'lowercase', 'number', 'specialChar']}
                            value={watchNewPassword ?? ''}
                            valueAgain={watchNewPasswordConfirmed ?? ''}
                            minLength={8}
                            onChange={isValid => setIsValid(isValid)}
                            iconComponents={{
                                validIcon: <SuccessIconNoBackground />,
                                invalidIcon: <DefaultIcon />,
                            }}
                            messages={{
                                minLength: getLabelFallback(
                                    'PASSWORD_CHECKLIST_MIN_LENGTH_RULE_MESSAGE'
                                ),
                                capital: getLabelFallback(
                                    'PASSWORD_CHECKLIST_CAPITAL_RULE_MESSAGE'
                                ),
                                lowercase: getLabelFallback(
                                    'PASSWORD_CHECKLIST_LOWER_RULE_MESSAGE'
                                ),
                                number: getLabelFallback('PASSWORD_CHECKLIST_NUMBER_RULE_MESSAGE'),
                                specialChar: getLabelFallback(
                                    'PASSWORD_CHECKLIST_SPECIAL_CHAR_RULE_MESSAGE'
                                ),
                            }}
                        />
                        {errors?.newPassword?.message && (
                            <FormHelperText className="error-helper-text ml-14">
                                {errors?.newPassword?.message}
                            </FormHelperText>
                        )}
                    </div>
                    <div className="form-group new-password-confirmed">
                        <TextField
                            {...register('newPasswordConfirmed')}
                            required
                            className={`w-100 ${
                                errors?.newPasswordConfirmed?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.newPasswordConfirmed.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="newPasswordConfirmed"
                            variant="outlined"
                            type={showPassword.showCurrentPasswordConfirmed ? 'text' : 'password'}
                            autoComplete="on"
                            label={getLabelFallback(
                                'SETTINGS_UPDATE_PASSWORD_NEW_PASSWORD_CONFIRMED'
                            )}
                            helperText={errors?.newPasswordConfirmed?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        className={`${
                                            errors?.newPasswordConfirmed?.message ||
                                            formFieldStatus.newPasswordConfirmed.focus ||
                                            formFieldStatus.newPasswordConfirmed.success
                                                ? 'large'
                                                : ''
                                        }`}
                                        position="end"
                                    >
                                        {errors?.newPasswordConfirmed?.message ? (
                                            <ErrorIcon />
                                        ) : formFieldStatus.newPasswordConfirmed.success ? (
                                            <SuccessIcon />
                                        ) : formFieldStatus.newPasswordConfirmed.focus ? (
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() =>
                                                    setValue('newPasswordConfirmed', '')
                                                }
                                            />
                                        ) : null}
                                        <div
                                            className="input-field-icon"
                                            onClick={() =>
                                                setShowPassword({
                                                    ...showPassword,
                                                    showCurrentPasswordConfirmed:
                                                        !showPassword.showCurrentPasswordConfirmed,
                                                })
                                            }
                                        >
                                            {showPassword.showCurrentPasswordConfirmed ? (
                                                <VisibilityOn />
                                            ) : (
                                                <VisibilityOff />
                                            )}
                                        </div>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>

                    <div className="form-footer">
                        <button
                            data-element-id="Settings_Save"
                            className={`primary-button button-size-medium ${updateSettingsPasswordRequestStatus === 'LOADING' ? 'loading' : ''
                                }`}
                            type="submit"
                        >
                            {getLabelFallback('SETTINGS_UPDATE_PASSWORD_SAVE_CHANGES_CTA')}
                            {updateSettingsPasswordRequestStatus === 'LOADING' && <PreLoader />}
                        </button>
                        {updateSettingsPasswordRequestStatus === 'SUCCESS' && (
                            <div className="success-message">
                                <SuccessIconNoBackground />
                                <span className="secondary-message medium success bold">
                                    {getLabelFallback(
                                        'SETTINGS_UPDATE_PASSWORD_FORM_SUCCESS_MESSAGE'
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};
export default UpdatePassword;
